import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Format } from '../../../..'
import { Button, ButtonGroup } from '@homebotapp/hb-react-component-catalog'
import { Heading, ProConList, Strong, Pro, Info } from '../../../../shared/Gem/GemDetail'
import { Player } from '../../../../shared/Player'
import MonthlyPayment from '../../MonthlyPayment'
import { BuyersLink } from '../../BuyersLink'
import CTAHeading from '../../CTAHeading'
import FeeRange from '../../FeeRange'
import { selectInstantOfferEnabled } from '../../../../../store/selectors/powerUps'
import {
  selectNewHomeTotalEquity,
  selectBuyerNewHomeBudgetWithCap,
  selectEightyLTVNewHomeMonthlyPayment
} from '../../../../../store/selectors/home/equity/newHome'
import { selectNewHomeVideoId, selectNewHomeVideoType } from '../../../../../store/selectors/videos'
import { selectNewHomeMortgageRate } from '../../../../../store/selectors/mortgageRate'
import { selectShowBuyersLink } from '../../../../../store/selectors/routes'
import history from '../../../../../history'
import { newHome } from '../../../../../constants/equity'
import { OVERLAY_HELPERS } from '../../../../../constants/navigation'
import { FROM } from '../../../../../mappings/navigation'
import styles from './NewHome.module.scss'
import { InstantOfferButton } from '../../../../shared/AvmModule/InstantOffer/InstantOfferButton'
import useViewport from '../../../../../hooks/useViewport'
import { HOMEOWNER_DIGEST_PURCHASING_POWER_NEW_HOME_PANEL } from '../../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { selectHomebotListingsFeatureEnabled } from '../../../../../store/selectors/customerProfile'
import { useCreateHomeClientEvent } from '../../../../../hooks/useCreateHomeClientEvent'
import { ClientEventName } from '../../../../../api/gqlaxy/gql/generated/graphql'

const { NEW_HOME } = FROM

const MSG = defineMessages({
  verifySellingPrice: {
    id: 'Homeowner.purchasePower.dreamHomeButton.cma',
    defaultMessage: 'Verify selling price'
  }
})

export const NewHomeDetail = () => {
  const { isMedium } = useViewport()
  const intl = useIntl()
  const instantOfferEnabled = useSelector(selectInstantOfferEnabled)

  const budget = useSelector(selectBuyerNewHomeBudgetWithCap)
  const totalEquity = useSelector(selectNewHomeTotalEquity)
  const monthlyPayment = useSelector(selectEightyLTVNewHomeMonthlyPayment)
  const mortgageRate = useSelector(selectNewHomeMortgageRate)
  const showBuyersLink = useSelector(selectShowBuyersLink)

  const videoId = useSelector(selectNewHomeVideoId)
  const videoType = useSelector(selectNewHomeVideoType)
  const createNewHomeViewedClientEvent = useCreateHomeClientEvent({ eventName: ClientEventName.ViewedDreamHomePanel })

  const showListings = useSelector(selectHomebotListingsFeatureEnabled)

  useEffect(() => {
    createNewHomeViewedClientEvent()
  }, [])

  return (
    <>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.dreamHomeHeading'
          defaultMessage="Put your hard-earned equity ({totalEquity}) to work. While you don't have to, if you aim for a {percent} down payment, you could buy a new home worth up to around {budget}!"
          values={{
            percent: <Format.Percentage value={newHome.downPaymentPercent * 100} />,
            totalEquity: (
              <Strong>
                <Format.Currency abbreviate value={totalEquity} />
              </Strong>
            ),
            budget: (
              <Strong>
                <Format.Currency abbreviate value={budget} />
              </Strong>
            )
          }}
        />
      </Heading>
      {videoType && videoId && <Player className={styles.video} videoType={videoType} videoId={videoId} />}
      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeOne'
            defaultMessage='Your new monthly principal and interest payment would be around {payment} if you did a {loanTerm}-year mortgage at {rate}.'
            values={{
              rate: <Format.Rate value={mortgageRate} />,
              loanTerm: newHome.loanTerm,
              payment: <MonthlyPayment value={monthlyPayment} />
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeTwo'
            defaultMessage='Get more square footage, a larger home office, or switch neighborhoods - you have options!'
          />
        </Pro>
        <Info>
          <FeeRange />
        </Info>
      </ProConList>
      <CTAHeading className={styles.cta}>
        <FormattedMessage id='Homeowner.purchasePower.ctaHeading' defaultMessage="So, what's next?" />
      </CTAHeading>

      <ButtonGroup
        gap='1'
        width='100%'
        display='flex'
        justifyContent='center'
        orientation={isMedium ? 'vertical' : 'horizontal'}
      >
        {showBuyersLink && (
          <BuyersLink
            affordability={budget as number}
            from={NEW_HOME}
            tracking={{
              guid: '3ymrw9j9owDiUjWDLk6Hgg',
              ui_context: HOMEOWNER_DIGEST_PURCHASING_POWER_NEW_HOME_PANEL
            }}
          />
        )}

        {instantOfferEnabled ? (
          <InstantOfferButton
            tracking={{
              guid: 'cHEfFecVErSFwCB9hUZnVB',
              ui_context: HOMEOWNER_DIGEST_PURCHASING_POWER_NEW_HOME_PANEL
            }}
          />
        ) : (
          <Button
            onClick={() => {
              history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())
            }}
            tracking={{
              guid: 'nhnHW2k2ohrhmp9ATP3Npc',
              ui_context: HOMEOWNER_DIGEST_PURCHASING_POWER_NEW_HOME_PANEL,
              descriptor: MSG.verifySellingPrice.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.verifySellingPrice)}
          </Button>
        )}
      </ButtonGroup>
    </>
  )
}
